/**
 * Servicio de sincronización
 *
 * Sincroniza datos locales (IndexedDB) con Supabase.
 * Estrategia: Local-first con sync eventual.
 */

import { db } from '@/services/storage';
import { getSupabase, isSupabaseConfigured } from './supabaseClient';
import {
  completionToSupabase,
  completionFromSupabase,
  snapshotToSupabase,
  snapshotFromSupabase,
  achievementStateToSupabase,
  achievementStateFromSupabase,
  type SupabaseCompletion,
  type SupabaseDaySnapshot,
  type SupabaseAchievementState,
} from './mappers';
import { loadAchievementState, saveAchievementState } from '@/services/achievements';

/**
 * Estado de sincronización
 */
export interface SyncStatus {
  isOnline: boolean;
  lastSyncAt: string | null;
  pendingChanges: number;
  isSyncing: boolean;
  error: string | null;
}

/**
 * Resultado de sincronización
 */
export interface SyncResult {
  success: boolean;
  uploaded: number;
  downloaded: number;
  conflicts: number;
  error?: string;
}

/**
 * Subir registros pendientes a Supabase
 */
export async function uploadPendingRecords(): Promise<SyncResult> {
  if (!isSupabaseConfigured()) {
    return { success: false, uploaded: 0, downloaded: 0, conflicts: 0, error: 'Supabase no configurado' };
  }

  const supabase = getSupabase();
  if (!supabase) {
    return { success: false, uploaded: 0, downloaded: 0, conflicts: 0, error: 'Cliente no disponible' };
  }

  try {
    // Obtener registros pendientes
    const pendingCompletions = await db.completions
      .where('syncStatus')
      .equals('pending')
      .toArray();

    const pendingSnapshots = await db.daySnapshots
      .where('syncStatus')
      .equals('pending')
      .toArray();

    let uploaded = 0;

    // Subir completions (convertir a snake_case)
    if (pendingCompletions.length > 0) {
      const supabaseData = pendingCompletions.map(completionToSupabase);

      const { error } = await supabase
        .from('completions')
        .upsert(supabaseData, { onConflict: 'id' });

      if (!error) {
        // Marcar como sincronizados
        await Promise.all(
          pendingCompletions.map((c) =>
            db.completions.update(c.id, { syncStatus: 'synced' })
          )
        );
        uploaded += pendingCompletions.length;
      } else {
        console.error('Error uploading completions:', error);
      }
    }

    // Subir snapshots (convertir a snake_case)
    if (pendingSnapshots.length > 0) {
      const supabaseData = pendingSnapshots.map(snapshotToSupabase);

      const { error } = await supabase
        .from('day_snapshots')
        .upsert(supabaseData, { onConflict: 'date' });

      if (!error) {
        // Marcar como sincronizados
        await Promise.all(
          pendingSnapshots.map((s) =>
            db.daySnapshots.update(s.date, { syncStatus: 'synced' })
          )
        );
        uploaded += pendingSnapshots.length;
      } else {
        console.error('Error uploading snapshots:', error);
      }
    }

    return { success: true, uploaded, downloaded: 0, conflicts: 0 };
  } catch (error) {
    console.error('Error uploading:', error);
    return {
      success: false,
      uploaded: 0,
      downloaded: 0,
      conflicts: 0,
      error: error instanceof Error ? error.message : 'Error desconocido',
    };
  }
}

/**
 * Descargar registros desde Supabase
 */
export async function downloadRecords(since?: string): Promise<SyncResult> {
  if (!isSupabaseConfigured()) {
    return { success: false, uploaded: 0, downloaded: 0, conflicts: 0, error: 'Supabase no configurado' };
  }

  const supabase = getSupabase();
  if (!supabase) {
    return { success: false, uploaded: 0, downloaded: 0, conflicts: 0, error: 'Cliente no disponible' };
  }

  try {
    let downloaded = 0;
    let conflicts = 0;

    // Descargar completions
    let completionsQuery = supabase.from('completions').select('*');
    if (since) {
      completionsQuery = completionsQuery.gte('last_modified', since);
    }

    const { data: remoteCompletions, error: completionsError } = await completionsQuery;

    if (!completionsError && remoteCompletions) {
      for (const remoteData of remoteCompletions as SupabaseCompletion[]) {
        const remote = completionFromSupabase(remoteData);
        const local = await db.completions.get(remote.id);

        if (!local) {
          // No existe localmente, insertar
          await db.completions.add({ ...remote, syncStatus: 'synced' });
          downloaded++;
        } else if (new Date(remote.lastModified) > new Date(local.lastModified)) {
          // Remoto más reciente, actualizar
          await db.completions.put({ ...remote, syncStatus: 'synced' });
          downloaded++;
        } else if (local.syncStatus === 'pending') {
          // Conflicto: local modificado y remoto diferente
          conflicts++;
        }
      }
    }

    // Descargar snapshots
    let snapshotsQuery = supabase.from('day_snapshots').select('*');
    if (since) {
      snapshotsQuery = snapshotsQuery.gte('last_modified', since);
    }

    const { data: remoteSnapshots, error: snapshotsError } = await snapshotsQuery;

    if (!snapshotsError && remoteSnapshots) {
      for (const remoteData of remoteSnapshots as SupabaseDaySnapshot[]) {
        const remote = snapshotFromSupabase(remoteData);
        const local = await db.daySnapshots.get(remote.date);

        if (!local) {
          await db.daySnapshots.add({ ...remote, syncStatus: 'synced' });
          downloaded++;
        } else if (new Date(remote.lastModified) > new Date(local.lastModified)) {
          await db.daySnapshots.put({ ...remote, syncStatus: 'synced' });
          downloaded++;
        } else if (local.syncStatus === 'pending') {
          conflicts++;
        }
      }
    }

    return { success: true, uploaded: 0, downloaded, conflicts };
  } catch (error) {
    console.error('Error downloading:', error);
    return {
      success: false,
      uploaded: 0,
      downloaded: 0,
      conflicts: 0,
      error: error instanceof Error ? error.message : 'Error desconocido',
    };
  }
}

/**
 * Sincronización completa (upload + download)
 */
export async function syncAll(): Promise<SyncResult> {
  // Primero subir cambios locales
  const uploadResult = await uploadPendingRecords();
  if (!uploadResult.success) {
    return uploadResult;
  }

  // Luego descargar cambios remotos
  const downloadResult = await downloadRecords();

  // Sincronizar achievements
  const achievementsResult = await syncAchievements();
  if (!achievementsResult.success) {
    console.warn('Achievement sync failed:', achievementsResult.error);
    // No fallar el sync completo por achievements
  }

  const result: SyncResult = {
    success: downloadResult.success,
    uploaded: uploadResult.uploaded,
    downloaded: downloadResult.downloaded,
    conflicts: downloadResult.conflicts,
  };

  if (downloadResult.error) {
    result.error = downloadResult.error;
  }

  return result;
}

/**
 * Obtener número de cambios pendientes
 */
export async function getPendingChangesCount(): Promise<number> {
  const pendingCompletions = await db.completions
    .where('syncStatus')
    .equals('pending')
    .count();

  const pendingSnapshots = await db.daySnapshots
    .where('syncStatus')
    .equals('pending')
    .count();

  return pendingCompletions + pendingSnapshots;
}

/**
 * Verificar conectividad
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Sincronizar estado de achievements
 */
export async function syncAchievements(): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase no configurado' };
  }

  const supabase = getSupabase();
  if (!supabase) {
    return { success: false, error: 'Cliente no disponible' };
  }

  try {
    const localState = loadAchievementState();
    const localData = achievementStateToSupabase(localState);

    // Obtener estado remoto
    const { data: remoteData, error: fetchError } = await supabase
      .from('achievement_state')
      .select('*')
      .eq('id', 'main')
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 = no rows returned, which is ok for first sync
      console.error('Error fetching achievements:', fetchError);
      return { success: false, error: fetchError.message };
    }

    if (!remoteData) {
      // No hay datos remotos, subir locales
      const { error: insertError } = await supabase
        .from('achievement_state')
        .insert(localData);

      if (insertError) {
        console.error('Error inserting achievements:', insertError);
        return { success: false, error: insertError.message };
      }
    } else {
      // Hay datos remotos, comparar y merge
      const remote = remoteData as SupabaseAchievementState;
      const remoteState = achievementStateFromSupabase(remote);

      // Merge: tomar el máximo de cada stat y unir arrays
      const mergedState = {
        unlockedIds: [...new Set([...localState.unlockedIds, ...remoteState.unlockedIds])],
        lastUnlocked: localState.lastUnlocked || remoteState.lastUnlocked,
        stats: {
          currentStreak: Math.max(localState.stats.currentStreak, remoteState.stats.currentStreak),
          totalCompletions: Math.max(localState.stats.totalCompletions, remoteState.stats.totalCompletions),
          perfectDays: Math.max(localState.stats.perfectDays, remoteState.stats.perfectDays),
          hardDaysRecovered: Math.max(localState.stats.hardDaysRecovered, remoteState.stats.hardDaysRecovered),
          featuresUsed: [...new Set([...localState.stats.featuresUsed, ...remoteState.stats.featuresUsed])],
        },
      };

      // Guardar merged localmente
      saveAchievementState(mergedState);

      // Subir merged a Supabase
      const mergedData = achievementStateToSupabase(mergedState);
      const { error: updateError } = await supabase
        .from('achievement_state')
        .upsert(mergedData, { onConflict: 'id' });

      if (updateError) {
        console.error('Error updating achievements:', updateError);
        return { success: false, error: updateError.message };
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error syncing achievements:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido',
    };
  }
}
